import type { CheckInTemplate, Track } from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, IconButton } from '../../components/Button.js'
import { EmptyState } from '../../components/EmptyState.js'
import { useToast } from '../../components/Toast.js'
import { trackColour } from '../../charts/colours.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Templates.module.css'

/**
 * Check-ins: named sets of tracks recorded together (T-303).
 *
 * Their order here is the order of the buttons on the home screen, so the
 * most-used check-in can be put first — which matters more than it sounds,
 * because this is the screen that decides whether logging is one tap or three.
 */
export function TemplateListScreen() {
  const { repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()

  const [templates, setTemplates] = useState<CheckInTemplate[] | null>(null)
  const [tracks, setTracks] = useState<Track[]>([])
  const [logged, setLogged] = useState<Map<string, number>>(new Map())
  const [showArchived, setShowArchived] = useState(false)

  const reload = useCallback(async () => {
    const [all, allTracks, counts] = await Promise.all([
      repos.templates.list({ includeArchived: true }),
      repos.tracks.list({ includeArchived: true }),
      repos.templates.countsByTemplate(),
    ])
    setTemplates(all)
    setTracks(allTracks)
    setLogged(counts)
  }, [repos])

  useEffect(() => {
    void reload()
  }, [reload])

  const active = (templates ?? []).filter((t) => !t.archived)
  const archived = (templates ?? []).filter((t) => t.archived)

  const byId = new Map(tracks.map((track) => [track.id, track]))

  /** Tracks that were archived or deleted after the template was written. */
  function staleCount(template: CheckInTemplate): number {
    return template.trackIds.filter((id) => byId.get(id)?.archived !== false).length
  }

  /**
   * Whether starting this check-in would produce a flow with anything in it.
   *
   * A template whose tracks have all been archived resolves to no steps, and
   * offering to log it would open a flow with nothing to answer and no way
   * forward. Editing it is the useful action there, and it is the one offered.
   */
  function isLoggable(template: CheckInTemplate): boolean {
    return template.trackIds.some((id) => byId.get(id)?.archived === false)
  }

  async function move(from: number, to: number) {
    if (to < 0 || to >= active.length) {
      return
    }
    const order = active.map((t) => t.id)
    const [moved] = order.splice(from, 1)
    if (moved === undefined) {
      return
    }
    order.splice(to, 0, moved)
    await repos.templates.reorder([...order, ...archived.map((t) => t.id)])
    await reload()
  }

  async function duplicate(template: CheckInTemplate) {
    // Variants differ by a track or two, so copying is the normal way to make
    // the second one.
    const result = await repos.templates.duplicate(template.id, `${template.name} copy`)
    if (!result.ok) {
      show({ message: result.errors[0]?.message ?? 'Could not copy.', tone: 'danger' })
      return
    }
    await reload()
    void navigate(`/templates/${result.value.id}`)
  }

  async function remove(template: CheckInTemplate) {
    // Unlike a track, deleting a template destroys nothing: past entries keep
    // their templateId and stay attributable (T-107).
    await repos.templates.remove(template.id)
    await reload()
    show({ message: `Deleted ${template.name}. Entries it created are unaffected.` })
  }

  if (templates === null) {
    return (
      <>
        <h1>Check-ins</h1>
        <p>Loading…</p>
      </>
    )
  }

  return (
    <>
      <div className={styles['header']}>
        <h1>Check-ins</h1>
        <Button
          variant="primary"
          disabled={tracks.length === 0}
          onClick={() => void navigate('/templates/new')}
        >
          New check-in
        </Button>
      </div>

      {active.length === 0 ? (
        <EmptyState
          title="No check-ins yet"
          body={
            tracks.length === 0
              ? 'A check-in records several tracks together, so you need at least one track first.'
              : 'A check-in is a named set of tracks you record together — "Before eating", "Bedtime". They become the buttons on your home screen, which is what makes logging one tap instead of five.'
          }
          action={
            tracks.length === 0 ? (
              <Button variant="primary" onClick={() => void navigate('/tracks')}>
                Set up a track
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className={styles['list']}>
          {active.map((template, index) => {
            const stale = staleCount(template)
            return (
              <div key={template.id} className={styles['row']}>
                <span className={styles['actions']}>
                  <IconButton
                    label={`Move ${template.name} up`}
                    disabled={index === 0}
                    onClick={() => void move(index, index - 1)}
                  >
                    <Chevron up />
                  </IconButton>
                  <IconButton
                    label={`Move ${template.name} down`}
                    disabled={index === active.length - 1}
                    onClick={() => void move(index, index + 1)}
                  >
                    <Chevron />
                  </IconButton>
                </span>

                {/*
                  Tapping a check-in **starts it** (T-908). It is a thing you do
                  far more often than a thing you edit, and the editor keeps its
                  own way in beside it — which is the reachability trap
                  `shell.spec.ts` exists to catch.
                */}
                <button
                  type="button"
                  className={styles['body']}
                  /*
                    Named for what it does. Left to concatenation the name would
                    be "Morning Hunger Mood Logged 3 times" — everything on the
                    row read out as though it were the label. It still opens
                    with the visible name, which is what a speech-input user
                    will say (WCAG 2.5.3).
                  */
                  aria-label={`Log ${template.name}`}
                  disabled={!isLoggable(template)}
                  onClick={() => void navigate(`/log/${template.id}`)}
                >
                  <span className={styles['name']}>{template.name}</span>
                  <span className={styles['chips']}>
                    {template.trackIds.map((id) => {
                      const track = byId.get(id)
                      if (track === undefined) {
                        return null
                      }
                      return (
                        <span key={id} className={styles['chip']}>
                          <span
                            className={styles['chipDot']}
                            style={{ background: trackColour(id) }}
                            aria-hidden="true"
                          />
                          {track.name}
                        </span>
                      )
                    })}
                  </span>
                  <span className={styles['meta']}>
                    <span>
                      {(logged.get(template.id) ?? 0) === 0
                        ? 'Never logged'
                        : (logged.get(template.id) ?? 0) === 1
                          ? 'Logged once'
                          : `Logged ${String(logged.get(template.id) ?? 0)} times`}
                    </span>
                    {stale > 0 && (
                      <span className={styles['warning']}>
                        {stale === 1
                          ? '1 needs attention'
                          : `${String(stale)} need attention`}
                      </span>
                    )}
                  </span>
                </button>

                <span className={styles['actions']}>
                  <IconButton
                    label={`Edit ${template.name}`}
                    onClick={() => void navigate(`/templates/${template.id}`)}
                  >
                    <Pencil />
                  </IconButton>
                  <IconButton
                    label={`Duplicate ${template.name}`}
                    onClick={() => void duplicate(template)}
                  >
                    <Copy />
                  </IconButton>
                  <IconButton
                    label={`Archive ${template.name}`}
                    onClick={() => void repos.templates.archive(template.id).then(reload)}
                  >
                    <Box />
                  </IconButton>
                  <IconButton
                    label={`Delete ${template.name}`}
                    onClick={() => void remove(template)}
                  >
                    <Bin />
                  </IconButton>
                </span>
              </div>
            )
          })}
        </div>
      )}

      {archived.length > 0 && (
        <section className={styles['archived']}>
          <button
            type="button"
            className={styles['disclosure']}
            aria-expanded={showArchived}
            onClick={() => setShowArchived((open) => !open)}
          >
            Archived ({archived.length})
          </button>
          {showArchived && (
            <div className={styles['list']}>
              {archived.map((template) => (
                <div key={template.id} className={styles['row']}>
                  <span />
                  <span className={styles['body']}>
                    <span className={styles['name']}>{template.name}</span>
                  </span>
                  <span className={styles['actions']}>
                    <IconButton
                      label={`Restore ${template.name}`}
                      onClick={() =>
                        void repos.templates
                          .update(template.id, { archived: false })
                          .then(reload)
                      }
                    >
                      <Restore />
                    </IconButton>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}
    </>
  )
}

function Chevron({ up = false }: { readonly up?: boolean }) {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d={up ? 'M5 12l5-5 5 5' : 'M5 8l5 5 5-5'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Pencil() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M3 17h3.5L16 7.5a1.8 1.8 0 0 0-2.5-2.5L4 14.5V17z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Copy() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M7 7V4h9v9h-3M4 7h9v9H4z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Box() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M3 6h14M4 6v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V6M8 10h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Bin() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Restore() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M4 10a6 6 0 1 1 1.8 4.3M4 6v4h4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
